/**
 * Shared Slack / Google Chat destination types + response extractors.
 *
 * Used by the per-routine `RoutineChannelPicker` (via `useChannelPickerState`)
 * so the response-shape parsing for both providers lives in ONE place.
 */

export interface SlackChannel {
  id: string;
  name: string;
  is_member: boolean;
}

export interface GoogleChatSpace {
  /** Space resource name, e.g. "spaces/AAAA..." — used as the stable identifier */
  name: string;
  /** Human-readable label */
  displayName: string;
  /** SPACE | DIRECT_MESSAGE | GROUP_CHAT */
  spaceType?: string;
}

/**
 * Parse the `channels` array out of a Slack `list_conversations` response.
 * Each element is guarded so a null / non-object / id-less entry is skipped
 * rather than throwing on property access.
 */
export const extractChannels = (responseData: unknown): SlackChannel[] => {
  const channels = (responseData as { channels?: unknown } | undefined)?.channels;
  if (!Array.isArray(channels)) return [];
  return channels.flatMap((item): SlackChannel[] => {
    if (!item || typeof item !== 'object') return [];
    const ch = item as { id?: unknown; name?: unknown; is_member?: unknown };
    if (typeof ch.id !== 'string') return [];
    return [{
      id: ch.id,
      name: typeof ch.name === 'string' && ch.name ? ch.name : ch.id,
      is_member: ch.is_member === true,
    }];
  });
};

/**
 * Extract spaces from the GOOGLE_CHAT_LIST_SPACES response.
 * Composio wraps Google Chat's `{ spaces: [...] }` body under `response.items[0].data`.
 * If the array isn't where we expect, warn so a Composio shape change is visible
 * instead of silently producing an empty dropdown.
 */
export const extractSpaces = (responseData: unknown): GoogleChatSpace[] => {
  const data = responseData as Record<string, unknown> | undefined;
  const raw = data?.spaces;
  if (!Array.isArray(raw)) {
    console.warn('GOOGLE_CHAT_LIST_SPACES returned no `spaces` array', responseData);
    return [];
  }
  return raw.flatMap((item): GoogleChatSpace[] => {
    if (!item || typeof item !== 'object') return [];
    const s = item as Record<string, unknown>;
    const name = typeof s.name === 'string' ? s.name : '';
    if (!name) return [];
    const displayNameValue = typeof s.displayName === 'string' ? s.displayName : '';
    const spaceTypeValue = typeof s.spaceType === 'string' ? s.spaceType : undefined;
    return [{ name, displayName: displayNameValue || name, spaceType: spaceTypeValue }];
  });
};
